/**
 * Helpers for GPU accelerated picking (finding objects from a 3D scene based on a cursor position on screen).
 * @module
 */
import { INTEGER_ID_OCCLUDER, INTEGER_ID_TRANSPARENT } from "common/constants/pickingConstants";
import { aabb2ContainsAABB2, aabb2Copy, aabb2Grow } from "geom/aabb2";
import { aabb2Height, aabb2New } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import * as THREE from "three";
import type { HighlightDataTexture } from "./HighlightDataTexture";
import { pickingMaterial } from "./materials/pickingMaterial";
import { checkWebGLContextLost } from "./threejs/higharc/checkWebGLContextLost";
import type { SavedCameraPosition } from "./threejs/higharc/SavedCameraPosition";
import {
  savedCameraPositionFromCamera,
  savedCameraPositionMatches,
  savedCameraPositionNew,
} from "./threejs/higharc/SavedCameraPosition";

const restoreViewport = new THREE.Vector4();
const perspectiveCamera: THREE.PerspectiveCamera = new THREE.PerspectiveCamera();
const orthographicCamera: THREE.OrthographicCamera = new THREE.OrthographicCamera(-1, 1, 1, -1);

// We limit the render target size to maintain performance and stability. It doesn't need to be resized even if the window is resized.
const PICKING_RENDER_TARGET_SIZE = 1024;

// In case there's no rectangular area picking happening on the page, we can save 4 MB by not pre-allocating the large picking buffer.
const PICKING_BUFFER_INITIAL_SIZE = 4;

const renderTarget: THREE.WebGLRenderTarget = new THREE.WebGLRenderTarget(
  PICKING_RENDER_TARGET_SIZE,
  PICKING_RENDER_TARGET_SIZE,
  { stencilBuffer: true },
);
let pixelBuffer: Uint8Array = new Uint8Array(4 * PICKING_BUFFER_INITIAL_SIZE * PICKING_BUFFER_INITIAL_SIZE);

interface CachedPickingRect {
  scene: THREE.Scene | undefined;
  sceneGeneration: number;
  canvasPixelAabb: AABB2;
  pixelWidth: number;
  pixelHeight: number;
  cameraPosition: SavedCameraPosition;
}
const cachedPickingRect: CachedPickingRect = {
  scene: undefined,
  sceneGeneration: 0,
  canvasPixelAabb: aabb2New(0, 0, 0, 0),
  pixelWidth: 1,
  pixelHeight: 1,
  cameraPosition: savedCameraPositionNew(),
};

// We encode picking ids a few bits apart to make differences more visible in a debug visualization.
// Similarly, adding 64 makes it easy to tell which objects have picking ids (even if it's 0) and which don't in a debug visualization.
function encodeByte(src) {
  return src * 3 + 64;
}

function decodeByte(src: number) {
  return (src - 64) / 3;
}

export const decodeIdGLSL = `
float decodeId(vec4 id) {
  float byte1 = (id.x * 255.0 - 64.0) / 3.0;
  float byte2 = (id.y * 255.0 - 64.0) / 3.0;
  float byte3 = (id.z * 255.0 - 64.0) / 3.0;
  return byte1 * 4096.0 + byte2 * 64.0 + byte3;
}
`;

/**
 * Encode an id to a float vector that can be used in a vertex attribute array.
 */
export function encodeIdToVec4(id: number | undefined, target: Float32Array, index: number = 0) {
  if (id === undefined || id === INTEGER_ID_OCCLUDER) {
    // This is the same value as the default GLES vertex input value in case the buffer is not bound.
    // (GLES 3.0 section 2.8 Vertex Specification)
    // By default meshes without an integer id buffer will just occlude other objects without being pickable.
    target[index + 0] = 0.0;
    target[index + 1] = 0.0;
    target[index + 2] = 0.0;
    target[index + 3] = 1.0;
    return;
  }
  if (id === INTEGER_ID_TRANSPARENT) {
    target[index + 0] = 0.0;
    target[index + 1] = 0.0;
    target[index + 2] = 0.0;
    target[index + 3] = 0.0;
    return;
  }
  target[index + 0] = encodeByte((id >> 12) & 63) / 255;
  target[index + 1] = encodeByte((id >> 6) & 63) / 255;
  target[index + 2] = encodeByte(id & 63) / 255;
  target[index + 3] = 1.0;
}

export function decodeVec4ToId(src: Float32Array, index: number = 0): number {
  if (src[index] === 0.0) {
    return INTEGER_ID_OCCLUDER;
  }
  return (
    (decodeByte(Math.round(src[index] * 255)) << 12) |
    (decodeByte(Math.round(src[index + 1] * 255)) << 6) |
    decodeByte(Math.round(src[index + 2] * 255))
  );
}

/**
 * Decode an id from fixed point back to an integer.
 */
function decodeIdFromFixedPoint(pixelBuffer: Uint8Array, pixelOffset: number = 0) {
  return (
    (decodeByte(pixelBuffer[pixelOffset * 4]) << 12) |
    (decodeByte(pixelBuffer[pixelOffset * 4 + 1]) << 6) |
    decodeByte(pixelBuffer[pixelOffset * 4 + 2])
  );
}

export function convertIdArrayToVec4Array(
  ids: Uint32Array | (number | undefined)[],
  target?: Float32Array,
): Float32Array {
  if (!target) {
    target = new Float32Array(ids.length * 4);
  }
  for (let i = 0; i < ids.length; ++i) {
    encodeIdToVec4(ids[i], target, i * 4);
  }
  return target;
}

function renderPickingBuffer(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  canvasPixelAabb: AABB2,
  canvasPixelWidth: number,
  canvasPixelHeight: number,
  highlightDataTexture?: HighlightDataTexture,
): [number, number] {
  const canvasAreaWidth = aabb2Width(canvasPixelAabb);
  const canvasAreaHeight = aabb2Height(canvasPixelAabb);

  // Render at half resolution to reduce the amount of data to process for large selection boxes.
  const pixelWidth = Math.min(Math.ceil(canvasAreaWidth * 0.5), PICKING_RENDER_TARGET_SIZE);
  const pixelHeight = Math.min(Math.ceil(canvasAreaHeight * 0.5), PICKING_RENDER_TARGET_SIZE);

  let pickingCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  if ((camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
    perspectiveCamera.copy(camera as THREE.PerspectiveCamera);
    pickingCamera = perspectiveCamera;
  } else {
    orthographicCamera.copy(camera as THREE.OrthographicCamera);
    pickingCamera = orthographicCamera;
  }
  pickingCamera.setViewOffset(
    canvasPixelWidth,
    canvasPixelHeight,
    canvasPixelAabb.minX,
    canvasPixelAabb.minY,
    canvasAreaWidth,
    canvasAreaHeight,
  );

  scene.overrideMaterial = pickingMaterial;
  pickingMaterial.defines.ISOLATE_MODE = 1;
  pickingMaterial.uniforms.highlightedIds.value = highlightDataTexture?.texture ?? null;
  pickingMaterial.uniforms.isolateMode.value = highlightDataTexture?.isolateMode ?? false;

  renderer.getViewport(restoreViewport);

  try {
    // Disable dither - potentially more stable output
    renderer.state.disable(renderer.getContext().DITHER);

    renderer.setRenderTarget(renderTarget);
    // The 1.00001 multiplier is added to ensure that the viewport is rounded correctly if pixel ratio is applied (WebIDL rounds towards 0)
    renderer.setViewport(
      0,
      0,
      (pixelWidth * 1.00001) / renderer.getPixelRatio(),
      (pixelHeight * 1.00001) / renderer.getPixelRatio(),
    );
    renderer.clear(true, true, false);
    renderer.render(scene, pickingCamera);

    const maxPixelBufferBytes = 4 * PICKING_RENDER_TARGET_SIZE * PICKING_RENDER_TARGET_SIZE;
    if (pixelBuffer.length < Math.min(maxPixelBufferBytes, 4 * pixelWidth * pixelHeight)) {
      pixelBuffer = new Uint8Array(maxPixelBufferBytes);
    }
    renderer.readRenderTargetPixels(renderTarget, 0, 0, pixelWidth, pixelHeight, pixelBuffer);

    scene.overrideMaterial = null;
  } catch (e) {
    checkWebGLContextLost(renderer, e, "Reading pixels failed.");
  }

  renderer.state.enable(renderer.getContext().DITHER);
  renderer.setViewport(restoreViewport);

  return [pixelWidth, pixelHeight];
}

/**
 * Assumes that the scene geometry contains a vertex attribute named "id", encoded using encodeIdToVec4.
 * Ignores the view offset of the camera, but otherwise uses the passed in camera parameters as they are.
 */
export function pickAtPixelRect(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  sceneGeneration: number,
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  canvasPixelAabb: AABB2,
  canvasPixelWidth: number,
  canvasPixelHeight: number,
  highlightDataTexture?: HighlightDataTexture,
): number[] {
  const idSet: Set<number> = new Set();

  const canvasAreaWidth = aabb2Width(canvasPixelAabb);
  const canvasAreaHeight = aabb2Height(canvasPixelAabb);

  if (
    cachedPickingRect.scene !== scene ||
    cachedPickingRect.sceneGeneration !== sceneGeneration ||
    !aabb2ContainsAABB2(cachedPickingRect.canvasPixelAabb, canvasPixelAabb) ||
    !savedCameraPositionMatches(camera, cachedPickingRect.cameraPosition)
  ) {
    // Render a new picking buffer.
    const canvasRenderAabb = aabb2Copy(canvasPixelAabb);

    if (canvasAreaWidth > 2 || canvasAreaHeight > 2) {
      // Render the picking buffer in a larger size so we can reuse it if the user keeps growing the picking area.
      const growX = Math.max(0, (canvasPixelWidth - aabb2Width(canvasRenderAabb)) / 2);
      const growY = Math.max(0, (canvasPixelHeight - aabb2Height(canvasRenderAabb)) / 2);
      aabb2Grow(canvasRenderAabb, growX, growY);
    }

    const [pixelWidth, pixelHeight] = renderPickingBuffer(
      renderer,
      scene,
      camera,
      canvasRenderAabb,
      canvasPixelWidth,
      canvasPixelHeight,
      highlightDataTexture,
    );
    cachedPickingRect.scene = scene;
    cachedPickingRect.sceneGeneration = sceneGeneration;
    cachedPickingRect.canvasPixelAabb = canvasRenderAabb;
    cachedPickingRect.pixelWidth = pixelWidth;
    cachedPickingRect.pixelHeight = pixelHeight;
    savedCameraPositionFromCamera(camera, cachedPickingRect.cameraPosition);
  }

  const cachedCanvasAreaWidth = aabb2Width(cachedPickingRect.canvasPixelAabb);
  const cachedCanvasAreaHeight = aabb2Height(cachedPickingRect.canvasPixelAabb);
  const pixelStartX = Math.max(
    0,
    Math.floor(
      ((canvasPixelAabb.minX - cachedPickingRect.canvasPixelAabb.minX) / cachedCanvasAreaWidth) *
        cachedPickingRect.pixelWidth,
    ),
  );

  // Note that Y is flipped
  const pixelStartY = Math.max(
    0,
    Math.floor(
      ((cachedPickingRect.canvasPixelAabb.maxY - canvasPixelAabb.maxY) / cachedCanvasAreaHeight) *
        cachedPickingRect.pixelHeight,
    ),
  );

  const subRectPixelWidth = Math.min(
    cachedPickingRect.pixelWidth - pixelStartX,
    Math.ceil((canvasAreaWidth / cachedCanvasAreaWidth) * cachedPickingRect.pixelWidth),
  );
  const subRectPixelHeight = Math.min(
    cachedPickingRect.pixelHeight - pixelStartY,
    Math.ceil((canvasAreaHeight / cachedCanvasAreaHeight) * cachedPickingRect.pixelHeight),
  );
  for (let pixelY = 0; pixelY < subRectPixelHeight; ++pixelY) {
    for (let pixelX = 0; pixelX < subRectPixelWidth; ++pixelX) {
      const id = decodeIdFromFixedPoint(
        pixelBuffer,
        (pixelStartY + pixelY) * cachedPickingRect.pixelWidth + pixelStartX + pixelX,
      );
      if (id >= 0) {
        idSet.add(id);
      }
    }
  }

  return [...idSet];
}
